import { Component, OnDestroy, OnInit, output, OutputEmitterRef } from '@angular/core';
import {
    CellClassParams,
    CellClickedEvent,
    ColDef,
    ColumnMovedEvent,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { UIRouter } from '@uirouter/angular';
import { AgGridColumns, AgGridOptionsBuilder } from '../../../Compliance/AgGrid';
import { AgGridExtensionsModule } from '../../../Compliance/AgGrid/agGridExtensions.module';
import { WsAgGridWrapperComponent } from '../../../Compliance/AgGrid/AgGridWrapper/wsAgGridWrapper.component';
import { AgGridRowCountComponent } from '../../../Compliance/AgGrid/Row-Count/agGridRowCount.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { PopoverModule } from 'ngx-bootstrap/popover';
import {
    ATTACHMENT_LOCATION_AVAILABLE_COLUMNS,
    ATTACHMENT_LOCATION_VISIBLE_COLUMNS,
    AttachmentBundlerService,
} from '../attachmentBundler.service';
import { takeUntil } from 'rxjs/operators';
import { DocumentService } from '../../../Documents/document.service';
import { PopUpService } from '../../../Common/Popup/popup.service';
import { AttachmentService } from '../../attachment.service';
import { SelectComponent } from '../../../UI-Lib/Select/select.component';
import { PicklistComponent } from '../../../UI-Lib/Picklist/picklist.component';
import {
    AgGridLinkCellRendererComponent,
    AgGridLinkCellRendererParams,
} from '../../../Compliance/AgGrid/CellRenderers/agGridLinkCellRenderer.component';

@Component({
    selector: 'bundler-selection-list',
    templateUrl: './bundlerSelectionList.component.html',
    styleUrls: ['./bundlerSelectionList.component.scss'],
    imports: [
        WsAgGridWrapperComponent,
        AgGridRowCountComponent,
        ReactiveFormsModule,
        SelectComponent,
        PicklistComponent,
        PopoverModule,
        AgGridExtensionsModule,
    ],
})
export class BundlerSelectionListComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _fb: FormBuilder,
        private readonly _attachmentBundlerService: AttachmentBundlerService,
        private readonly _documentService: DocumentService,
        private readonly _popUpService: PopUpService,
        private readonly _attachmentService: AttachmentService,
        private readonly _uiRouter: UIRouter,
    ) {}

    formValid: OutputEmitterRef<boolean> = output();

    form: FormGroup = this._fb.group({
        outputFormat: [null, Validators.required],
        namingConvention: [null],
        includeManifest: [false],
    });
    showZipForm: boolean = false;

    outputFormatOptions: Compliance.NameValuePair<Core.AttachmentBundlerGenerateFileOutputTypeEnum>[] = [
        {
            name: 'Combined PDF',
            value: Core.AttachmentBundlerGenerateFileOutputTypeEnum.PDF,
        },
        {
            name: 'Zip',
            value: Core.AttachmentBundlerGenerateFileOutputTypeEnum.Zip,
        },
    ];

    gridOptions = new AgGridOptionsBuilder({
        onSortChanged: () => this._sortChanged(),
        onColumnMoved: (event: ColumnMovedEvent) => {
            if (event.finished) {
                this._updateColumnsForManifest();
            }
        },
    })
        .withColumnResize()
        .withSort()
        .withMultipleColumnSort()
        .withLoadingOverlay()
        .withContext(this)
        .build();

    gridApi: GridApi;

    fileNameFormat: string[];
    readonly emptyFileNamePreviewText: string = 'No filename preview available';
    fileNameColumns: Compliance.NameValuePair<string>[] = [];
    hasDuplicateNames: boolean = false;
    hasTruncatedNames: boolean = false;
    hasOverlongNames: boolean = false;

    private readonly _longFileNameWarningLength = 100;
    private readonly _maxFileNameLength = 255;
    private readonly _invalidFileNameCharsRegex = /[<>:"/\\|?*\x00-\x1F\x7F]/g;
    private readonly _trailingInvalidWindowsCharsRegex = /[ .]+$/g;
    private readonly _reservedWindowsNameRegex = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i;
    private readonly _columnTypeMap: Map<string, Core.AttachmentBundlerItemPropertyEnum> = new Map([
        ['location', Core.AttachmentBundlerItemPropertyEnum.AttachmentLocation],
        ['attachmentType', Core.AttachmentBundlerItemPropertyEnum.AttachmentType],
        ['image', Core.AttachmentBundlerItemPropertyEnum.Image],
        ['topLevelCompanyName', Core.AttachmentBundlerItemPropertyEnum.TopLevelCompanyName],
        ['companyName', Core.AttachmentBundlerItemPropertyEnum.CompanyName],
        ['siteName', Core.AttachmentBundlerItemPropertyEnum.SiteName],
        ['parcelAccountNumber', Core.AttachmentBundlerItemPropertyEnum.ParcelAcctNum],
        ['propertyType', Core.AttachmentBundlerItemPropertyEnum.PropertyTypeAbbr],
        ['state', Core.AttachmentBundlerItemPropertyEnum.State],
        ['assessorName', Core.AttachmentBundlerItemPropertyEnum.Assessor],
        ['collectorName', Core.AttachmentBundlerItemPropertyEnum.Collector],
        ['year', Core.AttachmentBundlerItemPropertyEnum.Year],
        ['additionalDetails', Core.AttachmentBundlerItemPropertyEnum.AdditionalDetails],
        ['generatedFileName', Core.AttachmentBundlerItemPropertyEnum.GeneratedFileName],
    ]);
    private _duplicateRowIndices: Set<System.Guid> = new Set();
    private _truncatedRowIndices: Set<System.Guid> = new Set();
    private _overlongRowIndices: Set<System.Guid> = new Set();
    private _rowData: Core.AttachmentBundlerGenerateFileItemModel[] = [];
    private _destroy$: Subject<void> = new Subject();

    ngOnInit(): void {
        this._attachmentBundlerService.selectedAttachments$.pipe(takeUntil(this._destroy$)).subscribe((data) => {
            this._rowData = data;
        });

        this.form.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => {
            this.formValid.emit(this.form.valid);
        });

        this.form
            .get('outputFormat')
            .valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe((data) => {
                const isZip = data === Core.AttachmentBundlerGenerateFileOutputTypeEnum.Zip;
                if (this.gridApi) {
                    this.gridApi.setGridOption('columnDefs', this._getColumnDefs(isZip));
                }
                this.showZipForm = isZip;
                this._attachmentBundlerService.updateOutputType(data);

                if (!isZip) {
                    this._duplicateRowIndices.clear();
                    this._truncatedRowIndices.clear();
                    this._overlongRowIndices.clear();
                    this.hasDuplicateNames = false;
                    this.hasTruncatedNames = false;
                    this.hasOverlongNames = false;
                    if (this.gridApi) {
                        this.gridApi.redrawRows();
                    }
                } else {
                    const activeFileNameColumns = this.form.get('namingConvention').value;
                    this._generateFileNames(activeFileNameColumns || this.fileNameColumns);
                }
            });

        this.form
            .get('namingConvention')
            .valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe((nameColumns) => {
                this._generateFileNames(nameColumns);
            });

        this.form
            .get('includeManifest')
            .valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe(() => this._updateColumnsForManifest());

        this.formValid.emit(this.form.valid);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    get hasFileNamePreview(): boolean {
        return this.fileNamePreviewSegments.length > 0;
    }

    get fileNamePreviewSegments(): string[] {
        const nameColumns = this.form.get('namingConvention')?.value as Compliance.NameValuePair<string>[] | null;
        if (!nameColumns || !Array.isArray(nameColumns)) {
            return [];
        }

        return nameColumns.filter((y) => this._rowData.some((z) => z[y.value])).map((y) => y.name);
    }

    onAgGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;

        const columnDefs: ColDef[] = this._getColumnDefs();

        this.fileNameColumns = columnDefs
            .map((x) => ({ name: x.headerName, value: x.field }))
            .filter((x) => x.value !== 'image' && x.value !== 'location' && x.value !== 'attachmentDescription');

        const defaultSelected = this.fileNameColumns.filter((col) => {
            const uniqueValues = new Set(
                this._rowData.map((row) => {
                    const val = row[col.value];
                    return val == null || val === '' ? null : val;
                }),
            );
            return uniqueValues.size > 1;
        });
        this.form
            .get('namingConvention')
            .patchValue(defaultSelected.length > 0 ? defaultSelected : this.fileNameColumns);

        this.gridApi.setGridOption('columnDefs', columnDefs);
        this.gridApi.setGridOption('rowData', this._rowData);
    }

    private _getColumnDefs(isZip: boolean = false): ColDef[] {
        const allColumns: ColDef[] = [
            {
                headerName: 'Attachment Location',
                field: 'location',
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Attachment Type',
                field: 'attachmentType',
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Image',
                field: 'image',
                width: AgGridColumns.checkboxColumnMinWidth,
                cellRenderer: (params) => {
                    if (params.data?.attachmentId) {
                        if (params.colDef.pdfLoading && params.colDef.pdfLoading === params.value) {
                            return `<div class="text-center" style="margin-top: 0;">
                                            <i class="fa fa-spinner fa-pulse fa-1x"></i>
                                        </div>`;
                        } else {
                            return '<span class="anchor-style"><i class="fa fa-file-text-o" aria-hidden="true"></i></span>';
                        }
                    }
                    return '';
                },
                onCellClicked: async (params: CellClickedEvent) => {
                    const rowData = params.data;

                    params.colDef['pdfLoading'] = params.value;
                    params.api.redrawRows();

                    const attachmentId = rowData.attachmentId;

                    const response = await this._documentService.getDocumentBody(attachmentId);
                    const fileName = this._attachmentService.getFileNameFromHttpResponse(response);
                    const fileExtension = fileName.split('.').pop();
                    const attachmentBlob = new Blob([response.body], { type: fileExtension });
                    const fileInfo = this._attachmentService.getMimeTypeByFileExtension(fileExtension);

                    if (fileInfo['previewSupported']) {
                        const popUpData = {
                            attachmentInfo: {
                                attachmentID: attachmentId,
                                fileName: fileName,
                                fileExtension: fileExtension,
                            },
                        };

                        let attachmentGroupName = this._popUpService.StaticAliases.Attachment;

                        if (params.event['ctrlKey']) {
                            attachmentGroupName = attachmentGroupName + new Date().getTime();
                        }

                        const globalAttachmentsPopUpRef = this._popUpService.getByAliasGroup(attachmentGroupName)[0];
                        if (globalAttachmentsPopUpRef && globalAttachmentsPopUpRef.isOpen()) {
                            globalAttachmentsPopUpRef.publish(
                                this._popUpService.Topics.Attachment.AttachmentChanged,
                                popUpData,
                            );
                        } else {
                            const popUpRef = await this._popUpService.openAttachmentPreview(
                                attachmentGroupName,
                                globalAttachmentsPopUpRef,
                            );
                            popUpRef.publish(this._popUpService.Topics.Attachment.AttachmentChanged, popUpData);
                        }
                    } else {
                        await this._documentService.saveFile(attachmentBlob, fileName);
                    }

                    params.colDef['pdfLoading'] = undefined;
                    params.api.redrawRows();
                },
            },
            {
                headerName: 'Attachment Description',
                field: 'attachmentDescription',
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Top Level Company Name',
                field: 'topLevelCompanyName',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.topLevelCompanyName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.topLevelCompanyId) return '';
                        return this._uiRouter.stateService.href('company', {
                            companyId: params.data.topLevelCompanyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.topLevelCompanyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Company Name',
                field: 'companyName',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.companyName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.companyId) return '';
                        return this._uiRouter.stateService.href('company', { companyId: params.data.companyId });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Site Name',
                field: 'siteName',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.siteName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.siteId || !params.data?.companyId) return '';
                        return this._uiRouter.stateService.href('site', {
                            siteId: params.data.siteId,
                            companyId: params.data.companyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) =>
                        !params.data?.siteId || !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Parcel Acct Num',
                field: 'parcelAccountNumber',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.parcelAccountNumber,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.parcelId || !params.data?.siteId || !params.data?.companyId) return '';
                        return this._uiRouter.stateService.href('parcel', {
                            parcelId: params.data.parcelId,
                            siteId: params.data.siteId,
                            companyId: params.data.companyId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) =>
                        !params.data?.parcelId || !params.data?.siteId || !params.data?.companyId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Property Type Abbr',
                field: 'propertyType',
                width: AgGridColumns.textColumnSmallWidth,
            },
            {
                headerName: 'State',
                field: 'state',
                width: AgGridColumns.stateAbbreviationColumnWidth,
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.assessorName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.assessorId) return '';
                        return this._uiRouter.stateService.href('assessorRedirect', {
                            entityId: params.data.assessorId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.assessorId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Collector',
                field: 'collectorName',
                width: AgGridColumns.textColumnWidth,
                cellRenderer: AgGridLinkCellRendererComponent,
                cellRendererParams: {
                    newWindow: true,
                    getText: (params: AgGridLinkCellRendererParams) => params.data?.collectorName,
                    getLink: (params: AgGridLinkCellRendererParams) => {
                        if (!params.data?.collectorId) return '';
                        return this._uiRouter.stateService.href('collectorRedirect', {
                            entityId: params.data.collectorId,
                        });
                    },
                    isDisabled: (params: AgGridLinkCellRendererParams) => !params.data?.collectorId,
                } as AgGridLinkCellRendererParams,
            },
            {
                headerName: 'Year',
                field: 'year',
                width: AgGridColumns.numericColumnWidth,
            },
            {
                headerName: 'Additional Details',
                field: 'additionalDetails',
                width: AgGridColumns.textColumnWidth,
            },
        ];

        const locations = new Set(this._rowData.map((item) => item.location));
        const columnsToShow = new Set<string>(['location']);
        const availableToShow = new Set<string>();
        let visibleColumns = ['location'];

        locations.forEach((location) => {
            const locationColumns = ATTACHMENT_LOCATION_VISIBLE_COLUMNS[location];
            if (locationColumns) {
                locationColumns.forEach((col) => columnsToShow.add(col));
            } else {
                visibleColumns.forEach((col) => columnsToShow.add(col));
            }

            const locationAvailableColumns = ATTACHMENT_LOCATION_AVAILABLE_COLUMNS[location];
            if (locationAvailableColumns) {
                locationAvailableColumns.forEach((col) => availableToShow.add(col));
            }
        });

        visibleColumns = Array.from(columnsToShow);

        // Optional columns that are relevant to the data but hidden by default. Users can enable
        // them via the grid's column tool panel. Corresponds to the "orange" cells in the spec.
        const finalColumnSet = allColumns
            .filter((col) => visibleColumns.includes(col.field) || availableToShow.has(col.field))
            .map((col) => (visibleColumns.includes(col.field) ? col : { ...col, hide: true }));

        if (isZip) {
            finalColumnSet.unshift({
                headerName: 'File Name',
                field: 'generatedFileName',
                width: AgGridColumns.textColumnWidth,
                cellClass: (params: CellClassParams<Core.AttachmentBundlerGenerateFileItemModel>): string =>
                    this._isGeneratedFileNameWarning(params.data?.attachmentId) ? 'cell-validation-warning' : '',
            });
        }

        return finalColumnSet;
    }

    private _sortChanged(): void {
        const updatedData = [];
        this.gridApi.forEachNodeAfterFilterAndSort((node) => {
            updatedData.push(node.data);
        });
        this._attachmentBundlerService.setSelectedAttachments(updatedData);
    }

    private _updateColumnsForManifest() {
        const includeManifest = this.form.get('includeManifest').value;
        const fields = includeManifest
            ? this.gridApi
                  .getColumnDefs()
                  .filter((x: ColDef) => x.field !== 'image')
                  .map((x: ColDef) => this._columnTypeMap.get(x.field))
                  .filter((x) => x != null)
            : null;
        this._attachmentBundlerService.updateIncludeManifest(includeManifest, fields);
    }

    private _generateFileNames(nameColumns: Compliance.NameValuePair<string>[]): void {
        this._truncatedRowIndices.clear();
        this._duplicateRowIndices.clear();
        this._overlongRowIndices.clear();

        this.fileNameFormat = nameColumns.filter((y) => this._rowData.some((z) => z[y.value])).map((y) => y.name);

        const fileExtensions: string[] = this._rowData.map((x) => this._normalizeFileExtension(x.fileExtension));

        // Generate raw file names (before truncation)
        const rawNames: string[] = this._rowData.map((x) =>
            this._normalizeGeneratedFileBase(
                nameColumns
                    .map((y) => x[y.value])
                    .filter((y) => y != null && y !== '')
                    .join('-'),
            ),
        );

        // Count duplicates on candidate names (truncated to maxLength) for initial suffix length estimate
        const candidateCount = new Map<string, number>();
        rawNames.forEach((name, index) => {
            const fileExtension = fileExtensions[index];
            const candidate = name.substring(0, this._maxFileNameLength - fileExtension.length) + fileExtension;
            candidateCount.set(candidate, (candidateCount.get(candidate) || 0) + 1);
        });

        // Compute bases and suffix length; recompute if truncation merges entries beyond the reserved suffix
        let suffixLength = Math.max(3, `(${this._maxMapValue(candidateCount) - 1})`.length);
        let bases: string[];
        let baseCount: Map<string, number>;
        while (true) {
            bases = rawNames.map((name, index) => {
                const fileExtension = fileExtensions[index];
                return name.substring(0, this._maxFileNameLength - suffixLength - fileExtension.length);
            });
            baseCount = new Map<string, number>();
            bases.forEach((base, index) => {
                const key = base + fileExtensions[index];
                baseCount.set(key, (baseCount.get(key) || 0) + 1);
            });
            const requiredLength = `(${this._maxMapValue(baseCount) - 1})`.length;
            if (requiredLength <= suffixLength) {
                break;
            }
            suffixLength = requiredLength;
        }

        // Assign final file names, de-duplicating based on the base plus extension
        const baseOccurrence = new Map<string, number>();
        this._rowData.forEach((x, index) => {
            const rawName = rawNames[index];
            const fileExtension = fileExtensions[index];
            const base = bases[index];
            const key = base + fileExtension;
            const count = baseCount.get(key);

            if (rawName.length > this._longFileNameWarningLength) {
                this._overlongRowIndices.add(x.attachmentId);
            }

            if (count > 1) {
                const occurrence = baseOccurrence.get(key) || 0;
                baseOccurrence.set(key, occurrence + 1);
                this._duplicateRowIndices.add(x.attachmentId);

                if (occurrence === 0) {
                    // First occurrence - use just the base (no suffix)
                    x.generatedFileName = `${base}${fileExtension}`;
                } else {
                    // Subsequent occurrences - base + suffix
                    x.generatedFileName = `${base}(${occurrence})${fileExtension}`;
                }

                if (rawName.length > this._maxFileNameLength - suffixLength - fileExtension.length) {
                    this._truncatedRowIndices.add(x.attachmentId);
                }
            } else {
                // No collisions on this base - use full name truncated to maxLength
                const maxBaseLength = this._maxFileNameLength - fileExtension.length;
                x.generatedFileName = `${rawName.substring(0, maxBaseLength)}${fileExtension}`;
                if (rawName.length > maxBaseLength) {
                    this._truncatedRowIndices.add(x.attachmentId);
                }
            }
        });

        this.hasDuplicateNames = this._duplicateRowIndices.size > 0;
        this.hasTruncatedNames = this._truncatedRowIndices.size > 0;
        this.hasOverlongNames = this._overlongRowIndices.size > 0;
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', this._rowData);
        }
    }

    private _maxMapValue(map: Map<string, number>): number {
        let max = 0;
        for (const value of map.values()) {
            if (value > max) {
                max = value;
            }
        }
        return max;
    }

    private _normalizeFileExtension(fileExtension: string | null | undefined): string {
        const normalized = (fileExtension ?? '').trim().replace(/^\.+/, '').toLowerCase();
        const sanitized = this._sanitizeFileNamePart(normalized);
        return sanitized ? `.${sanitized}` : '';
    }

    private _normalizeGeneratedFileBase(value: string): string {
        const sanitized = this._sanitizeFileNamePart(value ?? '');
        if (!sanitized) {
            return 'file';
        }
        if (this._reservedWindowsNameRegex.test(sanitized)) {
            return `${sanitized}_`;
        }
        return sanitized;
    }

    private _sanitizeFileNamePart(value: string): string {
        return value.replace(this._invalidFileNameCharsRegex, '_').replace(this._trailingInvalidWindowsCharsRegex, '');
    }

    private _isGeneratedFileNameWarning(attachmentId: System.Guid | null | undefined): boolean {
        if (!attachmentId) {
            return false;
        }

        return (
            this._duplicateRowIndices.has(attachmentId) ||
            this._truncatedRowIndices.has(attachmentId) ||
            this._overlongRowIndices.has(attachmentId)
        );
    }
}
